import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, Form, Item, Menu } from 'semantic-ui-react'
import GridSearchTemplate from '../GridSearchTemplate';
import DropDown from "../../components/shared/dropdown";
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import RatesDropDown from '../../components/shared/RatesDropDown';
import { useMessageResources, useTextResources } from '../../language';
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import { formatValueAsCurrency, makeIntToDaysOfWeekArray } from '../../components/shared/utils'
import { Hotels, Promotions, Rates } from '../../api/services/next';
import { RateDiscountTypes, dateFormat, EntityType, DiscountApplicationTypes, RateDiscountTypesEnum, DiscountApplicationTypesEnum, ERateCategory } from '../../components/shared/constants';
import { onError } from '../../components/shared/messages';
import { DiscountPopup } from '../PriceList/Adjustments/DiscountPopup';
import moment from 'moment';
import { useConfirm } from "../../hooks/useConfirm";
import { endpoint } from '../../api/http';
import { ExcludedDatesPopup } from '../PriceList/Adjustments/ExcludedDatesPopup';
import "./style.css"
import { DatePicker } from '../../components/shared/DatePicker';
import { TrashButton, EditButton, IconButton } from '../../components/shared/icons';
import PromotionsDropDown from '../../components/shared/PromotionsDropDown';
import useBoolean from '../../hooks/useBoolean';
import EditHomeSitePromotionPopup from './popups/EditHomeSitePromotionPopup';
import CheckboxWrapper from '../../components/shared/Checkbox';
import ExcludedCustomerPopup from './popups/ExcludeCustomerPopup';
import { SyncRateToChannelManager } from '../../api/services/pms';
import { useLoader } from '../../hooks/useLoader';
import { ExcludedDatesButton } from './ExcludedDatesButton';
import PriceGroupsDropDown from "../../components/shared/PriceGroupsDropDown";
import ClassificationCategoriesDropDown from "../../components/shared/ClassificationCategoriesDropDown";

const Promotion = () => {
    const loader = useLoader();
    const text = useTextResources();
    const message = useMessageResources();
    const confirm  = useConfirm();
    const currentHotel = useCurrentHotel();

    const [search, setSearch] = useState({
        HotelIds: [currentHotel?.Id],
        RateDiscountTypes: [RateDiscountTypesEnum.OverallDiscount, RateDiscountTypesEnum.DiscountForEveryXNight],
        MultiRateDiscountsOnly: true,
		InActive: false,
    });
    const [selectedPromotion, setSelectedPromotion] = useState({});
    const [showDiscountPopup, setShowDiscountPopup] = useState(false);
    const [showExcludedPopup, setShowExcludedPopup] = useState(false);
    const [showExcludeCustomersPopup, setShowExcludeCustomersPopup] = useState(false);
    const [hotelsData, setHotelsData] = useState();

    const {
        value: shouldShowHomeSitePromotionPopup,
        setTrue: showHomeSitePromotionPopup,
        setFalse: hideHomeSitePromotionPopup,
    } = useBoolean(false);

    const [selectedDiscountType, setSelectedDiscountType] = useState(0);
    const [ratesData, setRatesData] = useState([]);
    const doSearch = useRef()
    useEffect(() => {
        (async () => {
            const hotels = await Hotels.GetHotels({
                IsActive: true,
                IncludeOnlyHotelsWithUserPermissions: true,
            });
            setHotelsData(hotels);
        })()
    }, [])

    useEffect(() => {
        if (search.HotelIds?.length) {
            (async () => {
                const rates = await Rates.GetRates({
                    HotelIds: search.HotelIds,
                    InheritedRates: true
                });
                setRatesData(rates);
            })()
        } else {
            setRatesData([]);
        }
    }, [search.HotelIds])

    const promotionsDropDownSpec = useMemo(
        () => ({
            HotelIds: search.HotelIds,
            ToDate: search.ToDate,
            FromDate: search.FromDate,
            RateId: search.RateId,
            RateDiscountTypes: search.RateDiscountTypes,
            MultiRateDiscountsOnly: true,
			InActive: search.IncludeInactive,
        }),
        [search]
    )

    const promotionFormatter = ({ row }) => {
        const hotelId = row.Rates[0]?.HotelId;
        const hotel = hotelsData?.find(h => h.Id === hotelId);


        return <Item key={row.Id}>
			<div className={row.IsActive ? '' : 'disabled'}>
				<Item.Header as='a'>{hotel?.Name} - {row.Name}</Item.Header>
				<Item.Meta>
					{datesRangeAndDaysFormatter(row)}
				</Item.Meta>
				<Item.Description>{getDiscountDescription(row)}</Item.Description>
				<Item.Description>{getDiscountSecondaryDescription(row)}</Item.Description>
			</div>
            <Item.Extra>
                <TrashButton floated="right" basic resetStyles={false} onClick={() => deletePromotionClicked(row)} disabled={!row.IsActive}/>
                <EditButton floated="right" basic resetStyles={false} onClick={() => editPromotionClicked(row)} />
                <IconButton floated="right" icon="sticky note outline" resetStyles={false} basic target="_blank" content="Content" href={`${endpoint}/PMS/Content/?EntityTypeId=${EntityType.Promotion}&EntityId=${row.Id}&HotelId=${row.Rates?.find(rate => rate.HotelId)?.HotelId}`} />
                {row.RateDiscountTypeId !== RateDiscountTypesEnum.HomeSiteDiscount && <ExcludedDatesButton RateExcludedDates={row.RateExcludedDates} onClick={() => excludedDatesClicked(row)}/>}
                <IconButton floated="right"  icon="users" basic content={text("Assignments")} resetStyles={false} onClick={() => excludeCustomersClicked(row)} />
            </Item.Extra>
        </Item>
    }

    const selectedDiscountTypeChanged = (value) => {
        setSelectedDiscountType(value);
        
        if (value === RateDiscountTypesEnum.HomeSiteDiscount) {
            setSelectedPromotion(undefined); // no need to intialize default state for home site discount
            showHomeSitePromotionPopup();
            return;
        } else if (value) {
            setSelectedPromotion({
                RateDiscountAmounts: [],
                ApplicableNight: value == RateDiscountTypesEnum.DiscountForEveryXNight ? 1 : null,
                RepeatDailyDiscount: false,
                RateDiscountTypeId: value,
                RateDiscountAssignments: [],
                MinimumNumberOfNights: 1,
                MaximumNumberOfNights: 999,
                PriceIncludesTax: false,
                DiscountApplicationTypeId: DiscountApplicationTypesEnum.Product,
                ClassificationCategoriesAssignments: [],
                RateExcludedDates: [],
            });
            setShowDiscountPopup(true);
        }
    }

    const getDiscountDescription = (discount) => {
        var discountValue = getDiscountValueDescription(discount.RateDiscountAmounts, discount.Rates?.[0]?.CurrencyId);
        var discountTitle = '';

        switch (discount.RateDiscountTypeId) {
            case RateDiscountTypesEnum.OverallDiscount:
                discountTitle = text("OverallPromotionDescription").replace("{0}", `${discountValue}`);
                break;
            case RateDiscountTypesEnum.DiscountForEveryXNight:
                discountTitle = text(discount.RepeatDailyDiscount ? "PromotionForEveryXNightDescription" : "PromotionForXNightDescription")
                    .replace("{0}", `${discountValue}`).replace("{1}", discount.ApplicableNight || 0);
                break;
            default:
                discountTitle = '';
                break;
        }
        return discountTitle;
    }
    const getDiscountSecondaryDescription = (discount) => {
        var discountTitle = '';

        var applicationType = DiscountApplicationTypes[discount.DiscountApplicationTypeId];
        var appliesOn = `${text("DiscountAppliesOn").replace("{0}", text(applicationType))}.`;

        discountTitle += appliesOn;

        if (discount.MinimumNumberOfNights || discount.MaximumNumberOfNights) {
            discountTitle += ` ${text("MinMaxOfNights").replace("{0}", discount.MinimumNumberOfNights || '_').replace("{1}", discount.MaximumNumberOfNights || '_')}`;
        }
        return discountTitle;
    }

    const getDiscountValueDescription = (amounts, currencyId) => {
        if (!amounts?.length) return '_';

        const isByAmount = amounts.find(a => a.DiscountAmount != null) != null;
        const propName = isByAmount ? 'DiscountAmountBeforeTax' : 'DiscountPercent';
        const values = amounts.map(a => a[propName]).filter(a => a != 0);
        const isSameValueForAll = values.every(v => v === values[0]);
        if (isByAmount) {
            return isSameValueForAll ? `${text('AmountBeforeTax')}: ${formatValueAsCurrency(values[0], currencyId)}` :
                `${text('DifferentAmounts')}`;
        }
        else {
            return isSameValueForAll ? `${values[0]}%` : `${text('DifferentPercentages')}`
        }
    }

    const deletePromotionClicked = async (promotion) => {
        await confirm({
            header: message('AreYouSure'),
            content: message('AreYouSureYouWouldLikeToDeleteSelectedItems'),
            confirmButton: {
                content: text('Yes'),
                positive: true,
            },
            cancelButton: {
                content: text('No'),
                negative: true,
            },
            onConfirm: () => {
                deletePromotion(promotion);
            },
        });
    }

    const excludedDatesClicked = (promotion) => {
        setSelectedPromotion(promotion);
        setShowExcludedPopup(true);
    }

    const editPromotionClicked = (promotion) => {
        setSelectedPromotion(promotion);
        setSelectedDiscountType(promotion.RateDiscountTypeId);
        if (promotion.RateDiscountTypeId === RateDiscountTypesEnum.HomeSiteDiscount) {
            showHomeSitePromotionPopup();
        } else {
            setShowDiscountPopup(true);
        }
    }

    const excludeCustomersClicked = (promotion) => {
        setSelectedPromotion(promotion);
        setSelectedDiscountType(promotion.RateDiscountTypeId);
        setShowExcludeCustomersPopup(true);
    }

    const deletePromotion = async (promotion) => {
        try {
            loader(true);
            await Promotions.DeletePromotions([promotion.Id]);
            await SyncRateToChannelManager({
                      PMSHotelID: promotion.Rates?.[0]?.HotelId,
                      ExternalPMSRateID: promotion.Id,
                      eRateCategory: ERateCategory.MultiRateDiscount
                    }) 
            await loadPromotions();
        } catch (error) {
            onError(error);
        }
        finally {
            loader(false);
        }
    }

    const datesRangeAndDaysFormatter = (row) => {
        const weekDaysArr = makeIntToDaysOfWeekArray(row.Weekdays);
        const daysOfStringWeek = (weekDaysArr.length == 0 || weekDaysArr.length == 7) ? text("AllDaysWeek") :
            `${weekDaysArr.indexOf(0) !== -1 ? text("Sunday") + ", " : ""}${weekDaysArr.indexOf(1) !== -1 ? text("Monday") +
                ", " : ""}${weekDaysArr.indexOf(2) !== -1 ? text("Tuesday") + ", " : ""}${weekDaysArr.indexOf(3) !== -1 ? text("Wednesday") +
                    ", " : ""}${weekDaysArr.indexOf(4) !== -1 ? text("Thursday") + ", " : ""}${weekDaysArr.indexOf(5) !== -1 ? text("Friday") +
                        ", " : ""}${weekDaysArr.indexOf(6) !== -1 ? text("Saturday") + ", " : ""}`.slice(0, -2);

        const datesRange = `${dateFormatter(row.StartDate)} - ${dateFormatter(row.EndDate)}`;

        return `${datesRange} : ${daysOfStringWeek}`;
    }

    const dateFormatter = (value) => {
        return value ? moment(value).format(dateFormat) : "";
    };

    const popupClosed = useCallback(() => {
        setSelectedDiscountType(0);
        setSelectedPromotion(null);
        setShowDiscountPopup(false);
        setShowExcludeCustomersPopup(false);
        hideHomeSitePromotionPopup();
    }, [hideHomeSitePromotionPopup])

    const excludedDatePopopClosed = useCallback(() => {
        setSelectedPromotion(null);
        setShowExcludedPopup(false);
    }, []);

    const loadPromotions = useCallback(() => {
        doSearch.current?.();
    }, []);

    const onSaveComplete = useCallback(() => {
        loadPromotions();
    }, [loadPromotions]);

    const onExcludedDateSaveComplete = useCallback(() => {
        loadPromotions();
    }, [loadPromotions]);
    
    const discountTypesDropDownData = useMemo(() => [
        { Id: RateDiscountTypesEnum.OverallDiscount, Name: text(RateDiscountTypes[RateDiscountTypesEnum.OverallDiscount]) },
        { Id: RateDiscountTypesEnum.DiscountForEveryXNight, Name: text(RateDiscountTypes[RateDiscountTypesEnum.DiscountForEveryXNight]) },
        { Id: RateDiscountTypesEnum.HomeSiteDiscount, Name: text(RateDiscountTypes[RateDiscountTypesEnum.HomeSiteDiscount]) },
    ],
        [text]
    );


    const gridOptions = {
        search: {
            fields: [
                <Form.Field width={4} placeholder={text('All')} multiple control={HotelsDropDown} label={text('Hotels')} key="HotelIds" value={currentHotel?.Id} data={hotelsData}/>,
                <DatePicker clearable label={text('StartDate')} key='FromDate' />,
                <DatePicker clearable label={text('EndDate')} key='ToDate' />,
                <Form.Field label={text("Rate")} key="RateId" placeholder={text('All')} clearable search control={RatesDropDown} data={ratesData} />,
                <Form.Field label={text("Promotions")} key="PromotionIds" placeholder={text('All')} multiple clearable search control={PromotionsDropDown} spec={promotionsDropDownSpec} />,
                <Form.Field width={2} label={text("RateDiscountType")} key="RateDiscountTypes" placeholder={text('All')} multiple clearable control={DropDown} data={discountTypesDropDownData} textField="Name" valueField="Id" />,
				<CheckboxWrapper label={text("IncludeInactive")} key="InActive" value={ false } />
            ],
            advancedFields: [
                <Form.Field
                key='PriceGroupIds'
                label={text("PriceGroup")}
                control={PriceGroupsDropDown}
                clearable
                multiple
                />,
                <Form.Field
                key='ClassificationCategoriesIds'
                label={text("PromotionCategory")}
                control={ClassificationCategoriesDropDown}
                multiple clearable
                />,
                <Form.Field 
                label={text("RateDiscountType")} 
                key="RateDiscountTypes" 
                placeholder={text('All')} 
                multiple clearable 
                control={DropDown} 
                data={discountTypesDropDownData} 
                textField="Name" 
                valueField="Id" 
                />,
            ],
            initial: search,
            searchOnLoad: true,
            spec: search,
            api: Promotions.GetPromotions,
            method: 'POST',
            setDoSearchRef: ref => doSearch.current = ref
        },
        grid: {
            columns: [
                { name: 'Selection', title: text('Promotion'), formatter: promotionFormatter },
            ],
            paging: {
                pageSize: 8
            },
            noVirtualTable: true,
            menu: [(
                <Menu key="new">
                    <Dropdown item text={text('New')}>
                        <DropdownMenu direction="left">
                            <DropdownItem onClick={()=>selectedDiscountTypeChanged(RateDiscountTypesEnum.OverallDiscount)}>{text(RateDiscountTypes[RateDiscountTypesEnum.OverallDiscount])}</DropdownItem>
                            <DropdownItem onClick={()=>selectedDiscountTypeChanged(RateDiscountTypesEnum.DiscountForEveryXNight)}>{text(RateDiscountTypes[RateDiscountTypesEnum.DiscountForEveryXNight])}</DropdownItem>
                            <DropdownItem onClick={()=>selectedDiscountTypeChanged(RateDiscountTypesEnum.HomeSiteDiscount)}>{text(RateDiscountTypes[RateDiscountTypesEnum.HomeSiteDiscount])}</DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </Menu>
            )]
        },
    };

    return (
      <main className='promotions-tab' style={{ height: '100%' }}>
        <div className='gridSearchTemplate'>
          <GridSearchTemplate
            options={gridOptions}
            onSearchChange={setSearch}
          />
        </div>
        {showDiscountPopup && (
          <DiscountPopup
            open={showDiscountPopup}
            onClose={popupClosed}
            discountForEdit={selectedPromotion}
            rateDiscountTypeToCreate={selectedDiscountType}
            onSaveComplete={onSaveComplete}
          />
        )}
        {showExcludedPopup && (
          <ExcludedDatesPopup
            open={showExcludedPopup}
            onClose={excludedDatePopopClosed}
            onSaveComplete={onExcludedDateSaveComplete}
            currentPromotion={selectedPromotion}
          />
        )}
        {shouldShowHomeSitePromotionPopup && (
          <EditHomeSitePromotionPopup
            open={shouldShowHomeSitePromotionPopup}
            onClose={popupClosed}
            homeSitePromotionId={selectedPromotion?.Id}
            onSave={onSaveComplete}
          />
        )}
        {showExcludeCustomersPopup && (
          <ExcludedCustomerPopup
            open={showExcludeCustomersPopup}
            onClose={popupClosed}
            currentPromotion={selectedPromotion}
            onSave={onSaveComplete}
          />
        )}
      </main>
    );
};
export default Promotion;
